import React from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PageLoader from './ui/PageLoader';

interface ProtectedOrganizerRouteProps {
  children: React.ReactNode;
}

/**
 * Route guard for the organizer surface.
 *
 * Mirrors ProtectedAdminRoute but allows organizers (and super admins for
 * support access). Organizer access is granted manually via the admin user
 * workflow or via invite codes that grant organizer capability — there is no
 * self-serve organizer onboarding.
 */
export const ProtectedOrganizerRoute: React.FC<ProtectedOrganizerRouteProps> = ({ children }) => {
  const { isAuthenticated, isOrganizer, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <PageLoader message="Checking access…" />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (!isOrganizer) {
    return (
      <div className="page flex items-center justify-center px-4">
        <div className="max-w-md mx-auto text-center card p-8">
          <h2 className="heading-section text-red-400 mb-2">Organizer access required</h2>
          <p className="text-gray-300 mb-4 text-sm">
            This area is for event organizers. If you should have access, contact the Bilt team.
          </p>
          <Link to="/garage" className="btn-secondary btn-md inline-flex">
            Back to Garage
          </Link>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
